import { AfterViewInit, Component, DOCUMENT, ElementRef, OnDestroy, OnInit, computed, inject, input, output, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'mbp-modal',
  templateUrl: './mbp-modal.component.html',
  styleUrl: './mbp-modal.component.scss',
  imports: [TranslateModule],
  host: { '(document:keydown)': 'onDocumentKeydown($event)' }
})
export class MbpModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  modalTitle = input.required<string>();
  busy = input(false);

  close = output<void>();

  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');
  private readonly footerContent = viewChild<ElementRef<HTMLElement>>('footerContent');
  private previouslyFocusedElement: HTMLElement | null = null;

  protected readonly hasFooter = computed(() => {
    const content = this.footerContent();
    if (!content) {
      return false;
    }
    return content.nativeElement.children.length > 0;
  });

  ngOnInit(): void {
    this.previouslyFocusedElement = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
    // Hoist the modal to <body> so it escapes any ancestor stacking context
    // (e.g. ion-content's shadow .inner-scroll has z-index: 0, which would trap
    // the modal below the right-side drawer). Angular still destroys the host
    // element correctly when the component is removed.
    this.document.body.appendChild(this.hostElement.nativeElement);
  }

  ngAfterViewInit(): void {
    this.dialog().nativeElement.focus();
  }

  ngOnDestroy(): void {
    // The modal hoisted its host element to <body> in ngOnInit. When the modal is nested
    // inside another component (e.g. add-user-modal), Angular removes the wrapper view but
    // not this reparented node, leaving an orphaned "ghost" modal in the DOM. Remove it
    // explicitly so it never lingers alongside a subsequently opened modal.
    this.hostElement.nativeElement.remove();
    this.previouslyFocusedElement?.focus();
  }

  protected onCloseClick(): void {
    if (this.busy()) {
      return;
    }
    this.close.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (this.busy()) {
      return;
    }
    if (event.target === event.currentTarget) {
      this.close.emit();
    }
  }

  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (this.document.querySelector('pwa-camera-modal-instance') !== null) {
      return;
    }

    if (event.key === 'Escape') {
      if (this.busy()) {
        return;
      }
      event.preventDefault();
      this.close.emit();
      return;
    }

    if (event.key === 'Enter') {
      this.onEnterKey(event);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    // Basic focus trap: keep Tab navigation inside the modal (WCAG AA)
    const dialogElement = this.dialog().nativeElement;
    const focusable = dialogElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : null;
    const activeInside = active !== null && dialogElement.contains(active);

    if (event.shiftKey && (!activeInside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!activeInside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  // Enter anywhere inside the modal triggers the footer's primary action (the mbp-button
  // marked with data-modal-primary) — but only when that button is enabled.
  private onEnterKey(event: KeyboardEvent): void {
    const target = event.target;
    // Scope to this modal instance: the handler is on document and modals can stack
    // (e.g. a confirmation modal on top of another modal). Only the modal that
    // contains the event target reacts.
    if (!(target instanceof HTMLElement) || !this.hostElement.nativeElement.contains(target)) {
      return;
    }

    // Enter in a textarea inserts a newline — never hijack it.
    if (target instanceof HTMLTextAreaElement) {
      return;
    }

    // Native activation wins for focused buttons/links (e.g. a focused Cancel button
    // must trigger Cancel, not the primary action) — don't double-fire.
    if (target.closest('button, a') !== null) {
      return;
    }

    const marker = this.footerContent()?.nativeElement.querySelector<HTMLElement>('[data-modal-primary]');
    const primaryButton = marker instanceof HTMLButtonElement ? marker : (marker?.querySelector('button') ?? null);
    if (primaryButton === null || primaryButton.disabled) {
      return;
    }

    event.preventDefault();
    primaryButton.click();
  }
}
